import React from 'react'
import InputLabel from '@mui/material/InputLabel'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import { makeStyles } from 'tss-react/mui'
import { TObject } from 'utils/types'

export type TInputEvent = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
export type TInputOnChange = (event: TInputEvent) => void

export interface ITextareaProps {
  name: string
  title?: string
  readOnly?: boolean
  disabled?: boolean
  borderColor?: string
  textColor?: string
  labelColor?: string
  formControlStyles?: TObject
  value?: string
  onChange?: TInputOnChange
  error?: null | string
  register?: React.Ref<any>
  rows?: number // количество строк
  hideErrorText?: boolean
  inputProps?: object
  defaultValue?: string
}

/**
 * Textarea field with label and error support
 */
export const Textarea: React.FC<ITextareaProps> = ({
  name,
  title,
  disabled = false,
  readOnly = false,
  labelColor = '',
  borderColor,
  textColor,
  formControlStyles = {},
  value,
  onChange,
  error,
  register,
  rows = 4,
  hideErrorText = true,
  inputProps,
  defaultValue,
}) => {
  const styles: Record<string, any> = {}
  const otherProps: Record<string, any> = {}

  if (borderColor || error) {
    styles.borderColor = error ? 'red' : borderColor
  }

  if (value !== undefined) {
    otherProps.value = value
  }

  if (defaultValue !== undefined) {
    otherProps.defaultValue = defaultValue
  }

  const { classes } = useStyles({ textColor })

  return (
    <FormControl style={{ ...formControlStyles, marginTop: 28 }} fullWidth>
      {title && (
        <InputLabel shrink={true} htmlFor={name} className={classes.label} style={{ color: labelColor }}>
          {title}
        </InputLabel>
      )}

      <TextField
        {...otherProps}
        multiline
        id={name}
        rows={rows}
        variant="outlined"
        name={name}
        onChange={onChange}
        inputRef={register}
        disabled={disabled}
        InputProps={{
          readOnly,
          classes: {
            input: classes.input,
            root: classes.inputRoot,
          },
          style: {
            color: textColor ? textColor : undefined,
            borderColor: borderColor ? borderColor : undefined,
            ...styles,
          },
          ...(inputProps as object),
        }}
      />

      {error && !hideErrorText && <div className={classes.error}>{error}</div>}
    </FormControl>
  )
}

const useStyles = makeStyles<{ textColor?: string }>()((theme, { textColor }) => ({
  label: {
    '&.MuiInputLabel-shrink': {
      left: '0 !important',
      top: '-30px !important',
    },
  },
  inputRoot: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'inherit',
      borderWidth: 1,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'inherit',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'inherit !important',
      borderWidth: 1,
      boxShadow: 'none !important',
    },
    borderColor: '#ABABAB',
  },
  input: {
    color: textColor ? textColor : theme.palette.text.primary,
    resize: 'vertical',
  },
  error: {
    color: 'red',
    fontSize: '0.75rem',
    marginLeft: 16,
    marginTop: 8,
  },
}))
